import React from "react";
import { List } from "react-window";
import { MoreVertical } from "lucide-react";
import {
  calculateProfileCompletion,
  getNameByIdFromList,
  formatDate,
  toTitleCase,
} from "../../lib/employeeHelpers";
import { DsBadge, DsButton, DsIcon } from "../../design-system";

const statusTone = (status) => {
  if (status === "Probation") return "warning";
  if (status === "Confirmed") return "success";
  if (status === "Resigned") return "danger";
  if (status === "Relieved") return "warning";
  return "neutral";
};

const VirtualEmployeeTable = ({
  employees,
  rowHeight,
  columnWidths,
  darkMode,
  handleEdit,
  handlePhotoClick,
  designationsList,
  departmentsList,
  branchesList,
  setProfileCompletionData,
  setShowProfileCompletionModal,
  actionButtonRefs,
  openActionDropdown,
  setOpenActionDropdown,
  setSelectedEmployee,
  setDropdownPosition,
  calculateDropdownPosition,
  API_URL,
  height,
}) => {
  const Row = ({ index, style }) => {
    const employee = employees[index];
    const completion = calculateProfileCompletion(employee);
    const pct = completion.percentage;

    return (
      <div
        style={{
          ...style,
          display: "flex",
          alignItems: "center",
        }}
        className="border-b border-[hsl(var(--ds-border))] transition-colors duration-150 hover:cursor-pointer hover:bg-[hsl(var(--ds-muted)/0.55)]"
        onDoubleClick={() => handleEdit(employee)}
      >
        <div
          className="flex items-center justify-center px-2 py-2 text-center font-semibold"
          style={{ width: columnWidths.actions }}
        >
          <div className="relative action-dropdown">
            <DsButton
              ref={(el) => {
                if (el && actionButtonRefs.current)
                  actionButtonRefs.current[employee.id] = el;
              }}
              type="button"
              variant="outline"
              size="sm"
              className="!h-8 !w-8 !rounded-full !px-0"
              onClick={(e) => {
                e.stopPropagation();
                const button = e.currentTarget;
                const rect = button.getBoundingClientRect();
                if (openActionDropdown === employee.id) {
                  setOpenActionDropdown(null);
                } else {
                  setOpenActionDropdown(employee.id);
                  setSelectedEmployee(employee);
                  if (calculateDropdownPosition) {
                    setDropdownPosition(calculateDropdownPosition(rect));
                  } else {
                    setDropdownPosition({
                      top: rect.bottom + window.scrollY + 4,
                      left: rect.left + window.scrollX,
                    });
                  }
                }
              }}
              title="Employee Actions"
            >
              <DsIcon icon={MoreVertical} size="sm" />
            </DsButton>
          </div>
        </div>

        <div
          className="flex items-center justify-center px-2 py-2 text-center font-semibold"
          style={{ width: columnWidths.photo }}
        >
          <div
            className="mx-auto h-8 w-8 cursor-pointer overflow-hidden rounded-full transition-transform duration-200 hover:scale-110"
            onClick={(e) => {
              e.stopPropagation();
              handlePhotoClick(employee);
            }}
            title="Click to view photo"
          >
            {employee.user?.photo ? (
              <img
                src={employee.user.photo}
                alt={`${employee.user?.name || "Employee Avatar"}`}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-[hsl(var(--ds-muted))] text-sm font-semibold text-[hsl(var(--ds-muted-fg))]">
                {employee.user?.name?.charAt(0)?.toUpperCase() || "E"}
              </div>
            )}
          </div>
        </div>

        <div
          className="flex items-center justify-center px-2 py-2 text-center font-semibold"
          style={{ width: columnWidths.slNo }}
        >
          {index + 1}
        </div>

        <div
          className="flex items-center justify-center px-2 py-2 text-center font-semibold"
          style={{ width: columnWidths.empCode }}
        >
          {employee.empCode}
        </div>

        <div
          className="flex items-center px-2 py-2 text-left font-semibold"
          style={{ width: columnWidths.name }}
        >
          {toTitleCase(
            `${employee.user?.name || ""} ${employee.user?.lastName || ""}`.trim()
          )}
        </div>

        <div
          className="flex items-center px-2 py-2 text-left font-medium"
          style={{ width: columnWidths.designation }}
        >
          {employee.designation?.name ||
            getNameByIdFromList(employee.designationId, designationsList)}
        </div>

        <div
          className="flex items-center px-2 py-2 text-left font-medium"
          style={{ width: columnWidths.department }}
        >
          {employee.department?.name ||
            getNameByIdFromList(employee.departmentId, departmentsList)}
        </div>

        <div
          className="flex items-center px-2 py-2 text-left font-medium"
          style={{ width: columnWidths.branch }}
        >
          {getNameByIdFromList(employee.branchId, branchesList)}
        </div>

        <div
          className="flex items-center justify-center px-2 py-2 text-center font-medium"
          style={{ width: columnWidths.doj }}
        >
          {formatDate(
            employee.dateOfJoin || employee.joiningDate || employee.joinDate
          )}
        </div>

        <div
          className="flex items-center justify-center px-2 py-2 text-center font-medium"
          style={{ width: columnWidths.mobile }}
        >
          {employee.user?.phone || "N/A"}
        </div>

        <div
          className="flex items-center overflow-hidden text-ellipsis whitespace-nowrap px-2 py-2 text-left font-medium"
          style={{ width: columnWidths.email }}
        >
          <div
            className="w-full truncate"
            title={
              typeof employee.email === "object" && employee.email !== null
                ? employee.email.name
                : employee.email || "N/A"
            }
          >
            {typeof employee.email === "object" && employee.email !== null
              ? employee.email.name
              : employee.email || "N/A"}
          </div>
        </div>

        <div
          className="flex items-center justify-center px-2 py-2 text-center font-medium"
          style={{ width: columnWidths.employmentType }}
        >
          {employee.employmentType}
        </div>

        <div
          className="flex items-center justify-center px-2 py-2 text-center font-medium"
          style={{ width: columnWidths.status }}
        >
          <DsBadge tone={statusTone(employee.employmentStatus)}>
            {employee.employmentStatus}
          </DsBadge>
        </div>

        <div
          className="flex items-center justify-center px-2 py-2 text-center font-semibold"
          style={{ width: columnWidths.profileCompletion }}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setProfileCompletionData({
                employee: employee,
                percentage: pct,
                pendingSections: completion.pendingSections,
              });
              setShowProfileCompletionModal(true);
            }}
            className={`flex cursor-pointer items-center justify-center rounded-full text-sm font-bold text-white transition-all duration-200 hover:scale-105 ${
              pct === 100 ? "h-9 w-9" : "h-10 w-10"
            } ${
              pct >= 80
                ? "bg-[hsl(var(--ds-success))] hover:opacity-90"
                : pct >= 60
                ? "bg-[hsl(var(--ds-warning))] hover:opacity-90"
                : "bg-[hsl(var(--ds-danger))] hover:opacity-90"
            }`}
            title="Click to view profile completion details"
          >
            {pct}%
          </button>
        </div>
      </div>
    );
  };

  return (
    <List
      height={height}
      itemCount={employees.length}
      itemSize={rowHeight}
      width="100%"
      className="employee-virtual-list"
    >
      {Row}
    </List>
  );
};

export default VirtualEmployeeTable;
